import { z } from "zod";
import type { CatalogMedia, CosplayPreferences } from "../domain/types";

const ENDPOINT = "https://graphql.anilist.co";
const QUERY = `
query HaebolcaeCatalog($page: Int!, $perPage: Int!, $type: MediaType, $genres: [String]) {
  Page(page: $page, perPage: $perPage) {
    media(type: $type, genre_in: $genres, isAdult: false, sort: [TRENDING_DESC, POPULARITY_DESC]) {
      id
      type
      siteUrl
      title { romaji english native }
      description(asHtml: false)
      seasonYear
      averageScore
      popularity
      genres
      tags { name rank isMediaSpoiler }
      coverImage { large color }
      characters(page: 1, perPage: 6) {
        edges { role node { id siteUrl name { full native } image { large } } }
      }
    }
  }
}`;

const responseSchema = z.object({
  data: z.object({
    Page: z.object({
      media: z.array(z.object({
        id: z.number(),
        type: z.enum(["ANIME", "MANGA"]),
        siteUrl: z.string().url(),
        title: z.object({ romaji: z.string().nullable(), english: z.string().nullable(), native: z.string().nullable() }),
        description: z.string().nullable(),
        seasonYear: z.number().nullable(),
        averageScore: z.number().nullable(),
        popularity: z.number().nullable(),
        genres: z.array(z.string()).nullable(),
        tags: z.array(z.object({ name: z.string(), rank: z.number(), isMediaSpoiler: z.boolean() })),
        coverImage: z.object({ large: z.string().url().nullable(), color: z.string().nullable() }),
        characters: z.object({
          edges: z.array(z.object({
            role: z.enum(["MAIN", "SUPPORTING", "BACKGROUND"]),
            node: z.object({
              id: z.number(),
              siteUrl: z.string().url(),
              name: z.object({ full: z.string(), native: z.string().nullable() }),
              image: z.object({ large: z.string().url().nullable() }),
            }),
          }).nullable()),
        }),
      })),
    }),
  }),
});

const wait = (milliseconds: number) => new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
const CACHE_TTL_MS = 5 * 60 * 1_000;
const catalogCache = new Map<string, { expiresAt: number; value: CatalogMedia[] }>();
const inFlight = new Map<string, Promise<CatalogMedia[]>>();

const cacheKeyFor = (preferences: CosplayPreferences) => JSON.stringify({
  mediaType: preferences.mediaType,
  genres: [...preferences.genres].sort(),
});

async function requestCatalog(preferences: CosplayPreferences, signal: AbortSignal, attempt = 0): Promise<CatalogMedia[]> {
  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      query: QUERY,
      variables: {
        page: 1,
        perPage: 30,
        type: preferences.mediaType,
        genres: preferences.genres.length > 0 ? preferences.genres : null,
      },
    }),
    signal,
  });

  if (response.status === 429 && attempt === 0) {
    const retrySeconds = Math.min(Number(response.headers.get("retry-after") ?? 2), 8);
    await wait(retrySeconds * 1_000);
    return requestCatalog(preferences, signal, 1);
  }
  if (!response.ok) throw new Error(`AniList 응답 ${response.status}`);

  const payload = responseSchema.parse(await response.json());
  const checkedAt = new Date().toISOString().slice(0, 10);
  return payload.data.Page.media.map((media) => ({
    id: String(media.id),
    type: media.type,
    title: media.title.english ?? media.title.romaji ?? media.title.native ?? "제목 미상",
    nativeTitle: media.title.native ?? undefined,
    siteUrl: media.siteUrl,
    coverImage: media.coverImage.large ?? undefined,
    color: media.coverImage.color ?? undefined,
    description: media.description?.replace(/<[^>]+>/g, "").slice(0, 240) ?? undefined,
    seasonYear: media.seasonYear ?? undefined,
    averageScore: media.averageScore ?? undefined,
    popularity: media.popularity ?? undefined,
    genres: media.genres ?? [],
    tags: media.tags.filter((tag) => !tag.isMediaSpoiler && tag.rank >= 50).slice(0, 8).map((tag) => tag.name),
    characters: media.characters.edges
      .filter((edge): edge is NonNullable<typeof edge> => edge !== null)
      .filter((edge) => edge.role !== "BACKGROUND" && Boolean(edge.node.image.large) && !/(^|\b)(narrator|narration)(\b|$)|ナレーター|語り手/i.test(`${edge.node.name.full} ${edge.node.name.native ?? ""}`))
      .map((edge) => ({
        id: String(edge.node.id),
        name: edge.node.name.full,
        nativeName: edge.node.name.native ?? undefined,
        siteUrl: edge.node.siteUrl,
        image: edge.node.image.large ?? undefined,
        role: edge.role,
      })),
    source: "AniList live",
    sourceCheckedAt: checkedAt,
  }));
}

export interface CatalogFetchOptions { force?: boolean }

export function clearAniListCatalogCacheForTests() {
  catalogCache.clear();
  inFlight.clear();
}

export async function fetchAniListCatalog(preferences: CosplayPreferences, options: CatalogFetchOptions = {}): Promise<CatalogMedia[]> {
  const key = cacheKeyFor(preferences);
  const cached = catalogCache.get(key);
  if (!options.force && cached && cached.expiresAt > Date.now()) return cached.value;
  const pending = inFlight.get(key);
  if (!options.force && pending) return pending;

  const request = (async () => {
    const controller = new AbortController();
    const timer = globalThis.setTimeout(() => controller.abort(new Error("AniList 요청 시간이 초과됐어요.")), 8_000);
    try {
      const result = await requestCatalog(preferences, controller.signal);
      if (result.length === 0) throw new Error("조건에 맞는 AniList 결과가 없어요.");
      catalogCache.set(key, { expiresAt: Date.now() + CACHE_TTL_MS, value: result });
      return result;
    } catch (error) {
      if (controller.signal.aborted) throw new Error("AniList 요청 시간이 초과됐어요.");
      throw error;
    } finally {
      globalThis.clearTimeout(timer);
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, request);
  return request;
}
